/**
 * 서버가 지시하는 **저장 포맷** — 웹 (PR 5a-B).
 *
 * 설계: `backend/docs/design/vault_write_format_channel/spec.md`
 *
 * 웹은 익스텐션과 구조가 다르다. 익스텐션은 저장 직전마다 `users/me` 를 받지만, 웹은
 * **로그인 응답**에서 암호 재료를 받아 메모리에 들고 쓴다(`pUtils.js:625`·`:628` →
 * 저장 시 `:1332-1336`). 그래서 포맷도 로그인 시점에 보관해뒀다가 저장 시 읽는다.
 *
 * 보관 수단은 기존 선례를 따른다 — `env.js:29-39` 의 `setTempEnvParam`/`getTempEnvParam`
 * (모듈 메모리 맵). `pUtils.js:434` 가 서버 응답 값을 같은 방식으로 넣고 `:1116` 이 읽는다.
 *
 * 읽기는 이 값과 무관하다 — PR 3 이 v1·v2 를 모두 읽게 만들었고 이 값은 **쓰기 전용 지시**다.
 */

import { encryptVaultBlobV2 } from 'services/vaultV2/format'

/** 구 포맷(AES-CBC). 값이 없거나 해석할 수 없으면 여기로 떨어진다. */
export const VAULT_WRITE_FORMAT_V1 = 1

/** 이 클라이언트가 쓸 수 있는 최고 포맷. 새 포맷 라이터는 PR 6 에서 생긴다. */
export const VAULT_WRITE_FORMAT_MAX_KNOWN = 2

/** `tempEnvParam` 보관 키. */
export const VAULT_WRITE_FORMAT_KEY = 'vaultWriteFormat'

/**
 * kdf 식별자 — 헤더 7번째 바이트에 그대로 들어간다.
 *
 * **파생을 생략한다.** 키 재료가 이미 고엔트로피 랜덤일 때 쓴다 (rsKey = 32바이트
 * `forge.random.getBytesSync`). 그런 키를 PBKDF2 로 스트레칭하는 것은 암호학적 이득이 없고
 * 복호마다 비용만 든다 (설계 D5).
 */
export const VAULT_KDF_NONE = 0x00

/** 저엔트로피 입력(마스터 비밀번호·UMK)에서 파생할 때. 반복수는 아래 상수를 쓴다. */
export const VAULT_KDF_PBKDF2 = 0x01

/**
 * **봉인 대상별 kdf 정책 — 이것이 정본이다.**
 *
 * 🛑 **여기 있는 이유는 순환 참조다.** 라이터(`pUtils.js`)와 스윕(`services/vaultV2/keyAdapters.js`)
 * 양쪽이 이 값을 써야 하는데, `keyAdapters` 가 `pUtils` 를 import 하므로(`keyAdapters.js:14`)
 * 반대 방향 import 는 `pUtils → keyAdapters → pUtils` 순환이 된다. 이 파일은 **양쪽이 이미
 * import 하고 있고** `pUtils` 를 참조하지 않는다(체인: `vaultWriteFormat → services/vaultV2/format
 * → attachmentV2/format` + forge).
 *
 * 🛑 **정책은 재료의 엔트로피가 정한다** (설계 D5):
 * - `group_key` — 32바이트 완전 난수(`pUtils.js:1375` `createRsKey()`)라 늘릴 무작위성이
 *   없다. PBKDF2 10만 회가 **보안을 전혀 올리지 않는다**
 * - `user_key` — 마스터 비밀번호에서 파생된 UMK 라 추측 가능하다. **빼면 사전 공격 비용이
 *   실제로 떨어진다**
 *
 * 🛑 **모르는 종류는 `VAULT_KDF_PBKDF2` 로 떨어뜨린다**(`kdfForKind` 의 기본값). 느릴 뿐
 * 약해지지 않는 방향이다 — 스윕은 열쇠 복호 실패 시 전체 중단이라 엄격하면 그 사용자가
 * 영구히 막힌다.
 *
 * 🛑 **리소스 항목(`resource_secret`)은 이 표를 타지 않는다.** `rsKeyObj.encrypt`
 * (`pUtils.js`)가 `kdf: VAULT_KDF_NONE` 을 직접 준다 — 그 경로는 `makeKeyCtx` 를 거치지
 * 않는다(호출처 실측: `keyAdapters.js` 2곳이 `user_key`·`group_key` 만 넘긴다).
 * 표에 `resource` 를 넣으면 **쓰이지 않는 항목**이 생기고, "모르는 종류" 테스트가
 * 그 이름을 쓰고 있어 판정이 갈린다.
 *
 * 이 표가 한 곳에 있어야 하는 이유: 라이터와 스윕이 갈리면 **같은 컬럼에 두 규격이 섞인다.**
 * 읽기는 정상이지만(판독기가 헤더로 갈라 읽는다) 어느 쪽이 정본인지 모호해진다.
 * 실제로 그렇게 갈렸던 것이 `backend/docs/design/group_key_kdf/` 가 고친 버그다.
 */
const KDF_BY_KIND = {
  group_key: VAULT_KDF_NONE,
  user_key: VAULT_KDF_PBKDF2,
}

/** 봉인 대상 종류로 kdf 를 정한다. 모르는 종류는 파생하는 쪽(안전한 방향). */
export function kdfForKind(kind) {
  return KDF_BY_KIND[kind] ?? VAULT_KDF_PBKDF2
}

/**
 * PBKDF2 반복수.
 *
 * 🛑 왕복은 이 값과 무관하게 성립한다 — 판독기가 헤더에 기록된 값으로 재파생한다
 * (`services/vaultV2/format.js`). 제약은 판독기가 허용하는 집합 안이어야 한다는 것뿐이고,
 * 100000 을 고른 이유는 구 포맷(`generateKey`)과 **같은 연산 비용**을 유지하기 위해서다.
 */
export const VAULT_PBKDF2_ITERATIONS = 100000

/**
 * 로그인 응답에서 저장 포맷을 꺼낸다.
 *
 * **없으면 구 포맷으로 떨어진다** (설계 D4) — 값이 없다고 저장을 막으면 조회 실패가
 * 사용자 입력 유실로 번진다.
 *
 * **모르는 값은 아는 최고 버전으로 강등한다** (설계 §4). 읽기 쪽은 반대로 엄격하다
 * (PR 3 — 미지 버전은 `UNSUPPORTED_VERSION`). 읽기는 **틀린 평문을 내지 않는 것**이,
 * 쓰기는 **계속 쓸 수 있는 것**이 우선이기 때문이다.
 *
 * ⚠️ 강등의 이점("계속 쓸 수 있다")은 **v2 라이터가 생긴 뒤에야** 유효하다. 지금은
 * `3 → 2 → encryptVaultBlob 예외` 라 강등해도 결국 못 쓴다. 오늘 서버가 `>1` 을 내보내지
 * 않아 도달 불가능하고, PR 6 에서 `3 → 2` 가 정상 동작하게 된다.
 */
export function readVaultWriteFormat(loginData) {
  const raw = loginData == null ? undefined : loginData.vault_format_version
  if (!Number.isInteger(raw) || raw < VAULT_WRITE_FORMAT_V1) {
    return VAULT_WRITE_FORMAT_V1
  }
  return Math.min(raw, VAULT_WRITE_FORMAT_MAX_KNOWN)
}

/** 새 포맷 라이터가 아직 없을 때 던진다 — 조용히 구 포맷으로 넘어가지 않는다. */
export class VaultWriteFormatError extends Error {
  constructor(message) {
    super(message)
    this.name = 'VaultWriteFormatError'
  }
}

/**
 * 서버가 지시한 포맷으로 금고 값을 암호화한다 — **갈림길**.
 *
 * 설계 D3 — 새 포맷 라이터는 PR 6 에서 생기므로 지금은 한쪽 팔이 비어 있다. 그래도 지금
 * 만드는 이유: ① 읽는 함수만 만들면 아무도 안 쓰는 코드가 된다 ② 새 포맷 팔을 조용히
 * 구 포맷으로 처리하면 **배선이 틀려도 결과가 정상처럼 보인다** — 이 저장소에서 그 유형이
 * 3회 재발했다(`planning-rules.md:70`).
 *
 * 구 포맷 암호화는 **thunk 로 주입받는다.** 이 모듈이 `pUtils` 를 import 하면 순환이 되기
 * 때문이다. PR 3 의 `decryptVaultBlob(blob, key, decryptV1Thunk)` 와 같은 관용구다.
 *
 * 🛑 **새 포맷 팔은 thunk 로 만들 수 없다** — thunk 는 암호화를 *수행해 결과를* 주는데
 *    새 포맷 인코더는 **평문과 키 재료**가 필요하다. 그래서 셋째 인자로 그 둘을 함께 받는다.
 *    호출부는 이미 둘 다 들고 있으므로(`encryptAESByKey(평문, 키)` 를 부르던 자리다) 추가
 *    비용이 없고, thunk 는 구 포맷 전용으로 남아 PR 5a 의 구조가 그대로 산다.
 *
 * 🛑 **주고받는 것은 바이트 문자열이다.** base64 는 호출부가 씌운다 — 판독기가 바이트
 *    문자열을 받고(`services/vaultV2/format.js`) 백엔드도 같은 구조다. 계약을 호출부마다
 *    다르게 두면 새 포맷 팔을 하나로 만들 수 없다.
 */
/**
 * 키 재료를 **구 포맷과 같은 바이트열**로 만든다.
 *
 * 🛑 이것이 없으면 **한글·일본어·이모지 마스터 비밀번호 사용자가 새 포맷에서 깨진다.**
 *    마스터 비밀번호는 길이 12 규칙만 있고 문자 종류 제한이 없어(`RegisterMasterPW` 의
 *    rules 에 `pattern` 이 0건) 그런 값이 실제로 존재하고, 지금 구 포맷에서 잘 동작한다.
 *
 * 구 포맷은 `Buffer.from(password, 'binary')` 로 **각 문자의 하위 8비트만** 취한다
 * (`pUtils.js` 의 `webCryptoPbkdf2`). 새 포맷의 `toKeyBytes` 는 `charCode > 0xFF` 에서
 * **예외**를 던지므로, 정규화 없이 넘기면 `KEY_MATERIAL_INVALID` 로 저장이 실패한다.
 *
 * UTF-8 로 인코딩하지 **않는** 이유: 그러면 구 포맷과 다른 키가 파생되어 **기존에 저장된
 * 값을 못 읽는다.** 절단을 이어받는 것이 호환을 지키는 유일한 방법이다.
 * (`Buffer.from(s, 'binary')` 와 바이트 단위로 일치함을 실측 확인 — PR #1986 리뷰)
 */
const toByteString = (s) => {
  let out = ''
  for (let i = 0; i < s.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    out += String.fromCharCode(s.charCodeAt(i) & 0xff)
  }
  return out
}

export async function encryptVaultBlob(format, encryptV1Thunk, options = {}) {
  if (format !== VAULT_WRITE_FORMAT_MAX_KNOWN) return encryptV1Thunk()

  const { plaintext, keyMaterial, kdf = VAULT_KDF_PBKDF2 } = options

  // 🛑 여기서 구 포맷으로 강등하지 않는다. 배선을 빠뜨린 것이지 "모르는 포맷"이 아니다 —
  //    강등하면 그 경로만 조용히 구 포맷으로 남아 배선 가드 밖에서 새어 나간다.
  //    "모르는 포맷"(위의 부등호)과 "배선 누락"(여기)은 다른 실패다.
  if (typeof plaintext !== 'string' || typeof keyMaterial !== 'string') {
    throw new VaultWriteFormatError(
      'vault write format v2 requires { plaintext, keyMaterial } as byte strings in options',
    )
  }

  return encryptVaultBlobV2(plaintext, toByteString(keyMaterial), {
    kdf,
    iterations: kdf === VAULT_KDF_NONE ? 0 : VAULT_PBKDF2_ITERATIONS,
  })
}
